import { SelectProps } from "@amzn/awsui-components-react";
import { CanonicalBusinessEvent } from "../../../types/cbes";
import { AccountingEventsGroup } from "../../../types/events";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../../types/documents";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
  isDigitalDocument,
} from "../../../helpers/typeChecks";
import {
  filterCbesByDestinationAndUsecase,
  groupCBEByUsecase,
} from "../../../helpers/cbeUtils";
import { Stage } from "../../../lusca-search-api/generated-src";

type JsonDataType =
  | CanonicalBusinessEvent
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
  | DigitalPaymentDocument;

export const createUsecaseOptions = (
  cbes: CanonicalBusinessEvent[]
): { value: string; label: string }[] => {
  const groupedCBEs = groupCBEByUsecase(cbes);
  return Array.from(groupedCBEs.keys()).map((usecase) => ({
    value: usecase,
    label: usecase,
  }));
};

export const getAvailableDestinations = (
  cbes: CanonicalBusinessEvent[],
  selectedUsecase: string
): Set<string> => {
  const destinations = new Set<string>();

  cbes.forEach((cbe) => {
    if (cbe.usecaseName === selectedUsecase && cbe.destination) {
      destinations.add(cbe.destination);
    }
  });

  return destinations;
};

const getVersionLabel = (
  data: JsonDataType,
  index: number,
  stage?: Stage
): string => {
  const prefix = stage ? `${stage} ` : "";

  if (isCanonicalBusinessEvent(data)) {
    return `${prefix}Version ${data.lockVersion}`;
  }

  if (isDigitalDocument(data)) {
    return `${prefix}Version ${data.version}`;
  }

  if (isAccountingEventsGroup(data)) {
    return `${prefix}Version ${data.sk}`;
  }

  return `${prefix}Version ${index + 1}`;
};

export const createVersionOptions = (
  data: JsonDataType[],
  usecase?: string,
  destination?: string
): SelectProps.Option[] => {
  if (!data?.length) return [];

  let processedData = data;

  if (isCanonicalBusinessEvent(data[0]) && usecase && destination) {
    processedData = filterCbesByDestinationAndUsecase(
      data as CanonicalBusinessEvent[],
      usecase,
      destination
    );
  }

  return processedData.map((item, index) => ({
    label: getVersionLabel(item, index),
    value: index.toString(),
  }));
};

export const createCrossStageVersionOptions = (
  leftData: JsonDataType[],
  rightData: JsonDataType[],
  currentStage: Stage,
  compareStage: Stage
): {
  leftOptions: SelectProps.Option[];
  rightOptions: SelectProps.Option[];
} => {
  const leftOptions = leftData.map((item, index) => ({
    label: getVersionLabel(item, index, currentStage),
    value: index.toString(),
  }));

  const rightOptions =
    rightData.length === 0
      ? [{ label: `${compareStage} - No data`, value: "0" }]
      : rightData.map((item, index) => ({
          label: getVersionLabel(item, index, compareStage),
          value: index.toString(),
        }));

  return { leftOptions, rightOptions };
};

export const getProductIdsFromCbes = (
  cbes: CanonicalBusinessEvent[],
  allProductIds: string[]
): string[] => {
  const found = new Set<string>();
  cbes.forEach((cbe) => {
    const match = allProductIds.find((pid) => cbe.idempotenceId.includes(pid));
    if (match) found.add(match);
  });
  return Array.from(found);
};

export const createProductOptions = (
  cbes: CanonicalBusinessEvent[],
  allProductIds: string[]
): SelectProps.Option[] => {
  const productIds = getProductIdsFromCbes(cbes, allProductIds);
  if (productIds.length <= 1) return [];
  return productIds.map((pid) => ({ label: pid, value: pid }));
};

export const filterCbesByProduct = (
  cbes: CanonicalBusinessEvent[],
  productId: string
): CanonicalBusinessEvent[] => {
  if (!productId) return cbes;
  return cbes.filter((cbe) => cbe.idempotenceId.includes(productId));
};

export const replaceIdInObject = (
  obj: unknown,
  originalId: string,
  overrideId: string
): unknown => {
  if (!originalId || !overrideId || originalId === overrideId) {
    return obj;
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === "string") {
    return obj.replace(new RegExp(originalId, "g"), overrideId);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => replaceIdInObject(item, originalId, overrideId));
  }

  if (typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const newKey = key.replace(new RegExp(originalId, "g"), overrideId);
      result[newKey] = replaceIdInObject(value, originalId, overrideId);
    }
    return result;
  }

  return obj;
};
